import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ExportToCsv } from 'export-to-csv';
import { TextFileValidators } from '../../../core/validators/text-file-validators';
import { AdminAuctionList } from '../../../models/adminAuctionList';
import constants from '../../../utility/constants';
import { ImportResultService } from '../service/import-result.service';
import {  Subscription } from 'rxjs';
export enum FileType {
  AuctionUser = 1,
  LotResult,
  ErrorLog,
}

@Component({
  selector: 'app-import-result',
  templateUrl: './import-result.component.html',
  styleUrls: ['./import-result.component.css'],
})
export class ImportResultComponent implements OnInit {
  @Input() auctionInfo: AdminAuctionList;

  processCompleted: boolean = false;
  showErrors: boolean = false;
  showSuccess: boolean = false;

  importForm: FormGroup;

  importFileType = FileType;

  fileSize: string = constants.admin.csvUploadSetting.lotImportCsvFileMaxSize;
  fileFormat: string = constants.admin.csvUploadSetting.lotImportCsvFileType;
  fileExtension: string =
    constants.admin.csvUploadSetting.lotImportCsvFileExtension;

  submitting: boolean = false;
  isLoading: boolean = false;

  errorsInFiles: {
    auctionUserErrors?: string;
    lotResultErrors?: string;
  };

  noOfLotsImported: number = 0;
  noOfUsersImported: number = 0;
  private subscriptions: Array<Subscription> = [];

  private validatorsForAuctionUser: ValidatorFn[] = [
    TextFileValidators.sizeCheck(this.fileSize),
    TextFileValidators.textExtension(this.fileExtension),
  ];

  private validatorsForLotResult: ValidatorFn[] = [
    TextFileValidators.sizeCheck(this.fileSize),
    TextFileValidators.textExtension(this.fileExtension),
  ];

  constructor(private importResult: ImportResultService) {}

  ngOnInit(): void {
    this.initiateForm();
    this.checkAuctionUsers();
  }

  private initiateForm() {
    this.importForm = new FormGroup({
      auctionUser: new FormControl(null, [
        Validators.required,
        ...this.validatorsForAuctionUser,
      ]),
      lotResult: new FormControl(null, [
        Validators.required,
        ...this.validatorsForLotResult,
      ]),
    });
  }

  private checkAuctionUsers() {
    this.isLoading = true;
    const aucUsers = this.importResult.checkAuctionUsers(this.auctionInfo.id).subscribe(
      (res) => {
        if (res) {
          this.importForm.controls.auctionUser.setValidators(
            this.validatorsForAuctionUser
          );
          this.importForm.controls.auctionUser.updateValueAndValidity();
        }
        this.isLoading = false;
      },
      () => (this.isLoading = false)
    );
    this.subscriptions.push(aucUsers);
  }

  downloadCsvFile(fileType: number) {
    let headers = [];
    let fileName: string = '';
    let exportedData = [];

    switch (fileType) {
      case this.importFileType.AuctionUser:
        headers = [
          'Bidder Number',
          'Company',
          'First Name',
          'Last Name',
          'Address Line 1',
          'Address Line 2',
          'City',
          'State',
          'Postal',
          'Country',
          'Phone',
          'Fax',
          'Email',
        ];
        fileName = `Sample_AuctionUser_${this.auctionInfo.auctionCode}`;
        exportedData = new Array(headers.length).fill('');
        this.generateCsv(headers, exportedData, fileName);
        break;
      case this.importFileType.LotResult:
        headers = [
          'Lot Number',
          'Description',
          'Amount',
          'Bidder Number',
          'Location',
        ];
        fileName = `Sample_LotResult_${this.auctionInfo.auctionCode}`;
        exportedData = new Array(headers.length).fill('');
        this.generateCsv(headers, exportedData, fileName);
        break;
      case this.importFileType.ErrorLog:
        fileName = `ErrorLog_${this.auctionInfo.auctionCode}`;
        for (const key in this.errorsInFiles) {
          headers.push(key);
          exportedData.push(this.errorsInFiles[key]);
        }
        this.generateCsv(headers, exportedData, fileName, true);
        break;
    }
  }

  private generateCsv(
    headers: string[],
    data: any,
    fileName: string,
    isErrorFile: boolean = false
  ) {
    const errorOptions = {
      filename: fileName,
      showLabels: true,
      useKeysAsHeaders: false,
      headers: headers,
      fieldSeparator: '',
      quoteStrings: '',
      useTextFile: true,
    };

    const options = {
      filename: fileName,
      showLabels: true,
      useKeysAsHeaders: false,
      headers: headers,
    };
    const csvExporter = new ExportToCsv(isErrorFile ? errorOptions : options);
    if (data) csvExporter.generateCsv(data);
  }

  uploadLotResult(files: FileList) {
    let control = this.importForm.controls.lotResult;
    if (files.length > 0) {
      control.setValue(files[0]);
      control.updateValueAndValidity();
      control.markAsTouched();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
  }

  uploadAuctionUser(files: FileList) {
    let control = this.importForm.controls.auctionUser;
    if (files.length > 0) {
      control.setValue(files[0]);
      control.updateValueAndValidity();
      control.markAsTouched();
    }
  }

  removeFile(control: AbstractControl) {
    control.reset();
  }

  async uploadFiles() {
    this.submitting = true;
    if (this.importForm.invalid) return;
    this.submitting = false;

    this.isLoading = true;

    this.processCompleted = false;
    this.showErrors = false;
    this.showSuccess = false;

    let lotResultFile = this.importForm.getRawValue().lotResult;
    let auctionUserFile = this.importForm.getRawValue().auctionUser;

    if (auctionUserFile) {
      try {
        this.noOfUsersImported = await this.importResult
          .uploadAuctionUsers(this.auctionInfo.id, auctionUserFile)
          .toPromise();
      } catch (error) {
        this.isLoading = false;
        this.processCompleted = true;
        this.showErrors = true;
        this.showSuccess = false;
        this.errorsInFiles = { auctionUserErrors: error };
        return;
      }
    }

    if (lotResultFile) {
      try {
        this.noOfLotsImported = await this.importResult
          .uploadLotResults(this.auctionInfo.id, lotResultFile)
          .toPromise();
      } catch (error) {
        this.isLoading = false;
        this.processCompleted = true;
        this.showErrors = true;
        this.showSuccess = false;
        this.errorsInFiles = { lotResultErrors: error };
        return;
      }
    }

    this.isLoading = false;
    this.processCompleted = true;
    this.showErrors = false;
    this.showSuccess = true;
  }
}
